import { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  PictureAsPdf,
  Payment,
  Block,
  Description,
} from "@mui/icons-material";
import {
  fetchInvoices,
  fetchInvoiceById,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  cancelInvoice,
  addRepayment,
  deleteRepayment,
  openInvoicePdf,
  fetchInvoiceCounts,
} from "../../api/InvoiceService";
import { fetchCustomerPage } from "../../api/CustomerService";
import AsyncAutocomplete from "../../components/common/AsyncAutocomplete";
import customerLabel from "../../utils/customerLabel";
import TableComponent from "../../components/Table";
import ConfirmDialog from "../../components/ConfirmDialog";
import CreateInvoiceDialog from "../../components/invoice/CreateInvoiceDialog";
import EditInvoiceDialog from "../../components/invoice/EditInvoiceDialog";
import PaymentsDialog from "../../components/invoice/PaymentsDialog";
import CreateHandoverLetterDialog from "../../components/handover/CreateHandoverLetterDialog";
import { createHandoverLetter } from "../../api/HandoverLetterService";
import { toast } from "react-toastify";
import theme from "../../theme/theme";

const STATUS_CONFIG = {
  pending: { bg: "#FFF8E1", color: "#F57F17", label: "Pending" },
  paid: { bg: "#E8F5E9", color: "#2E7D32", label: "Paid" },
  cancelled: { bg: "#FFEBEE", color: "#C62828", label: "Cancelled" },
};

const InvoicesManagementPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [links, setLinks] = useState([]);
  const [meta, setMeta] = useState({ from: 0, to: 0, total: 0 });

  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState({
    customer_id: "",
    search: "",
    from_date: "",
    to_date: "",
  });
  const [counts, setCounts] = useState({
    all: 0,
    pending: 0,
    paid: 0,
    cancelled: 0,
  });

  // Create
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErrors, setCreateErrors] = useState({});

  // Edit
  const [openEdit, setOpenEdit] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [fetchingInvoice, setFetchingInvoice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editErrors, setEditErrors] = useState({});

  // Delete
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Cancel Confirm
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Payments
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [paymentsInvoice, setPaymentsInvoice] = useState(null);
  const [addingPayment, setAddingPayment] = useState(false);

  // PDF
  const [pdfLoading, setPdfLoading] = useState(null);

  // Handover letter (generate from invoice)
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [handoverPrefill, setHandoverPrefill] = useState(null);
  const [handoverSaving, setHandoverSaving] = useState(false);
  const [handoverErrors, setHandoverErrors] = useState({});

  const statusForTab = ["", "pending", "paid", "cancelled"];

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const params = { ...filters, page: currentPage, per_page: perPage };
      const status = statusForTab[tabValue];
      if (status) params.status = status;

      const res = await fetchInvoices(params);
      const payload = res.data || res;
      setInvoices(payload?.data || []);
      setLinks(payload?.links || []);
      setMeta({ from: payload?.from || 0, to: payload?.to || 0, total: payload?.total || 0 });
    } catch (err) {
      toast.error(err.message || "Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const loadCounts = async () => {
    try {
      const res = await fetchInvoiceCounts(filters);
      setCounts(res.data?.data || res.data || { all: 0, pending: 0, paid: 0, cancelled: 0 });
    } catch {
      // silent
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, tabValue, perPage]);

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, tabValue, currentPage, perPage]);

  useEffect(() => {
    loadCounts();
  }, [filters]);

  // CREATE
  const handleCreate = async (data) => {
    setCreating(true);
    setCreateErrors({});
    try {
      await createInvoice(data);
      toast.success("Invoice created successfully");
      setOpenCreate(false);
      loadInvoices();
      loadCounts();
    } catch (err) {
      if (err?.response?.data?.errors) {
        setCreateErrors(err.response.data.errors);
      } else {
        toast.error(err.message || "Failed to create invoice");
      }
    } finally {
      setCreating(false);
    }
  };

  // EDIT
  const handleEditClick = async (row) => {
    setOpenEdit(true);
    setFetchingInvoice(true);
    setEditErrors({});
    try {
      const res = await fetchInvoiceById(row.id);
      setEditInvoice(res.data?.data || res.data || res);
    } catch {
      toast.error("Failed to fetch invoice details");
      setOpenEdit(false);
    } finally {
      setFetchingInvoice(false);
    }
  };

  const handleUpdate = async (data) => {
    if (!editInvoice) return;
    setSaving(true);
    setEditErrors({});
    try {
      await updateInvoice(editInvoice.id, data);
      toast.success("Invoice updated successfully");
      setOpenEdit(false);
      loadInvoices();
      loadCounts();
    } catch (err) {
      if (err?.response?.data?.errors) {
        setEditErrors(err.response.data.errors);
      } else {
        toast.error(err.message || "Failed to update invoice");
      }
    } finally {
      setSaving(false);
    }
  };

  // DELETE
  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteInvoice(deleteTarget.id);
      toast.success("Invoice deleted successfully");
      setConfirmOpen(false);
      setDeleteTarget(null);
      loadInvoices();
      loadCounts();
    } catch (err) {
      toast.error(err.message || "Failed to delete invoice");
    } finally {
      setDeleting(false);
    }
  };

  // CANCEL
  const handleCancelClick = (row) => {
    setCancelTarget(row);
    setConfirmCancelOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelInvoice(cancelTarget.id);
      toast.success("Invoice cancelled");
      setConfirmCancelOpen(false);
      setCancelTarget(null);
      loadInvoices();
      loadCounts();
    } catch (err) {
      toast.error(err.message || "Failed to cancel invoice");
    } finally {
      setCancelling(false);
    }
  };

  // PAYMENTS
  const handlePaymentsClick = async (row) => {
    try {
      const res = await fetchInvoiceById(row.id);
      setPaymentsInvoice(res.data?.data || res.data || res);
      setPaymentsOpen(true);
    } catch {
      toast.error("Failed to load invoice");
    }
  };

  const handleAddPayment = async (paymentData, resetForm) => {
    if (!paymentsInvoice) return;
    setAddingPayment(true);
    try {
      const res = await addRepayment(paymentsInvoice.id, paymentData);
      const updated = res.data?.data || res.data || res;
      setPaymentsInvoice(updated);
      toast.success("Payment added successfully");
      resetForm();
      loadInvoices();
      loadCounts();
    } catch (err) {
      toast.error(err.message || "Failed to add payment");
    } finally {
      setAddingPayment(false);
    }
  };

  const handleDeletePayment = async (repaymentId) => {
    if (!paymentsInvoice) return;
    try {
      const res = await deleteRepayment(paymentsInvoice.id, repaymentId);
      const updated = res.data?.data || res.data || res;
      setPaymentsInvoice(updated);
      toast.success("Payment deleted");
      loadInvoices();
      loadCounts();
    } catch (err) {
      toast.error(err.message || "Failed to delete payment");
    }
  };

  // PDF
  const handlePdf = async (row) => {
    setPdfLoading(row.id);
    try {
      await openInvoicePdf(row.id);
    } catch (err) {
      toast.error(err.message || "Failed to open PDF");
    } finally {
      setPdfLoading(null);
    }
  };

  // HANDOVER LETTER (prefilled from this invoice)
  const handleGenerateHandover = (row) => {
    setHandoverErrors({});
    setHandoverPrefill({
      customer_id: row.customer_id,
      total_price: row.total_amount,
      invoice_id: row.id,
      customer: row.customer || null,
    });
    setHandoverOpen(true);
  };

  const handleSaveHandover = async (data) => {
    setHandoverSaving(true);
    setHandoverErrors({});
    try {
      await createHandoverLetter(data);
      toast.success("Handover letter created successfully");
      setHandoverOpen(false);
      setHandoverPrefill(null);
    } catch (err) {
      if (err?.response?.data?.errors) {
        setHandoverErrors(err.response.data.errors);
      } else {
        toast.error(err.message || "Failed to create handover letter");
      }
    } finally {
      setHandoverSaving(false);
    }
  };

  const columns = [
    { field: "invoice_number", headerName: "Invoice #" },
    { field: "customer.name", headerName: "Customer" },
    {
      field: "invoice_date",
      headerName: "Date",
      render: (r) => {
        if (!r.invoice_date) return "-";
        return new Date(r.invoice_date).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      field: "invoice_ammount",
      headerName: "Amount",
      render: (r) => `₹${Number(r.invoice_ammount || 0).toFixed(2)}`,
    },
    {
      field: "total_received_amount",
      headerName: "Received",
      render: (r) => `₹${Number(r.total_received_amount || 0).toFixed(2)}`,
    },
    {
      field: "total_due_amount",
      headerName: "Due",
      render: (r) => {
        const due = Number(r.total_due_amount || 0);
        return (
          <span style={{ color: due > 0 ? "#C62828" : "#2E7D32", fontWeight: 600 }}>
            ₹{due.toFixed(2)}
          </span>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      render: (r) => {
        const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
        return (
          <Chip
            label={cfg.label}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: 11,
              backgroundColor: cfg.bg,
              color: cfg.color,
            }}
          />
        );
      },
    },
  ];

  const actions = [
    {
      icon: <PictureAsPdf />,
      label: "PDF",
      onClick: (row) => handlePdf(row),
      backgroundColor: "#ff9800",
    },
    {
      icon: <Payment />,
      label: "Payments",
      onClick: (row) => handlePaymentsClick(row),
      backgroundColor: theme.palette.primary.main,
      hide: (row) => row.status === "cancelled",
    },
    {
      icon: <Description />,
      label: "Handover Letter",
      onClick: (row) => handleGenerateHandover(row),
      backgroundColor: "#6A1B9A",
      hide: (row) => row.status === "cancelled",
    },
    {
      icon: <Edit />,
      label: "Edit",
      onClick: (row) => handleEditClick(row),
      backgroundColor: theme.button.editBackgroundColor,
      hide: (row) => row.status === "cancelled",
    },
    {
      icon: <Block />,
      label: "Cancel",
      onClick: (row) => handleCancelClick(row),
      backgroundColor: "#ff5252",
      hide: (row) => row.status === "cancelled" || row.status === "paid",
    },
    {
      icon: <Delete />,
      label: "Delete",
      onClick: (row) => handleDeleteClick(row),
      backgroundColor: theme.button.deleteBackgroundColor,
    },
  ];

  // Filter out hidden actions per row
  const getActions = () =>
    actions.map((a) => ({
      ...a,
      onClick: (row) => {
        if (a.hide && a.hide(row)) return;
        a.onClick(row);
      },
    }));

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          Invoices Management
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          sx={{
            borderRadius: theme.button.borderRadius,
            backgroundColor: theme.palette.primary.main,
          }}
          onClick={() => setOpenCreate(true)}
        >
          Create Invoice
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label={`All (${counts.all})`} />
          <Tab label={`Pending (${counts.pending})`} />
          <Tab label={`Paid (${counts.paid})`} />
          <Tab label={`Cancelled (${counts.cancelled})`} />
        </Tabs>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <AsyncAutocomplete
          label="Filter by Customer"
          value={filters.customer_id}
          onChange={(id) => setFilters({ ...filters, customer_id: id })}
          fetchPage={fetchCustomerPage}
          getOptionLabel={customerLabel}
          sx={{ minWidth: 220 }}
        />
        <TextField
          size="small"
          label="Search"
          placeholder="Invoice # or customer name"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          sx={{ minWidth: 250 }}
        />
        <TextField
          type="date"
          size="small"
          label="From Date"
          InputLabelProps={{ shrink: true }}
          value={filters.from_date}
          onChange={(e) =>
            setFilters({ ...filters, from_date: e.target.value })
          }
        />
        <TextField
          type="date"
          size="small"
          label="To Date"
          InputLabelProps={{ shrink: true }}
          value={filters.to_date}
          onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
        />
        {(filters.customer_id || filters.search || filters.from_date || filters.to_date) && (
          <Button
            variant="text"
            onClick={() => {
              setFilters({
                customer_id: "",
                search: "",
                from_date: "",
                to_date: "",
              });
            }}
          >
            Clear Filters
          </Button>
        )}
      </Box>

      {/* Table */}
      <Box>
        <TableComponent
          data={invoices || []}
          columns={columns}
          actions={getActions()}
          loading={loading}
          links={links}
          setCurrentPage={setCurrentPage}
          meta={meta}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </Box>

      {/* Dialogs */}
      <CreateInvoiceDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        saving={creating}
        onSave={handleCreate}
        errors={createErrors}
      />

      <EditInvoiceDialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setEditInvoice(null);
        }}
        saving={saving}
        loading={fetchingInvoice}
        onSave={handleUpdate}
        errors={editErrors}
        invoice={editInvoice}
      />

      <PaymentsDialog
        open={paymentsOpen}
        onClose={() => {
          setPaymentsOpen(false);
          setPaymentsInvoice(null);
        }}
        invoice={paymentsInvoice}
        onAddPayment={handleAddPayment}
        onDeletePayment={handleDeletePayment}
        adding={addingPayment}
      />

      <CreateHandoverLetterDialog
        open={handoverOpen}
        onClose={() => {
          setHandoverOpen(false);
          setHandoverPrefill(null);
        }}
        saving={handoverSaving}
        onSave={handleSaveHandover}
        errors={handoverErrors}
        prefill={handoverPrefill}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Invoice"
        message={`Are you sure you want to delete invoice ${deleteTarget?.invoice_number}? This action cannot be undone.`}
        loading={deleting}
      />

      <ConfirmDialog
        open={confirmCancelOpen}
        onClose={() => {
          setConfirmCancelOpen(false);
          setCancelTarget(null);
        }}
        onConfirm={handleConfirmCancel}
        title="Cancel Invoice"
        message={`Are you sure you want to cancel invoice ${cancelTarget?.invoice_number}?`}
        loading={cancelling}
      />
    </Box>
  );
};

export default InvoicesManagementPage;
